import { defineComponent, h } from 'vue';
import utils from '../../Shared/utils.jsx';
import BaseComponent from './BaseComponentMixin.jsx';
import EventBus from '@/Application/event-bus';
import { QAvatar, colors } from 'quasar';
import theme from '@/plugins/theme';

const CSS_VAR_PATTERN = /^var\(\s*(--[^,)\s]+)\s*(?:,\s*([^)]+))?\)$/;

function isCssColorValue(value) {
    if (value == null || value === '')
        return false;

    const resolved = String(value).trim();
    return resolved.startsWith('#')
        || resolved.startsWith('rgb')
        || resolved.startsWith('hsl')
        || resolved.startsWith('var(');
}

function resolveAvatarColor(color) {
    if (color == null || color === '')
        return '';

    const resolved = theme.resolveThemeColor(color);
    return isCssColorValue(resolved) ? resolved : '';
}

// Quasar applies palette names as a bg-* class, so probe that class to learn the real background.
// An unresolved name leaves the element transparent, which means no background was applied at all.
function resolvePaletteBackground(name) {
    if (typeof document === 'undefined')
        return '';

    const probe = document.createElement('div');
    probe.className = `bg-${name}`;
    probe.style.cssText = 'position:fixed;visibility:hidden;pointer-events:none;';
    document.body.appendChild(probe);
    const background = getComputedStyle(probe).backgroundColor;
    probe.remove();

    if (!background || background === 'transparent' || /^rgba\(\s*0,\s*0,\s*0,\s*0\s*\)$/.test(background))
        return '';

    return background;
}

// Flattens theme variables and Quasar palette names down to a literal color so its brightness can be measured.
function toLiteralColor(color) {
    if (!color)
        return '';

    let value = String(color).trim();

    const cssVar = CSS_VAR_PATTERN.exec(value);
    if (cssVar) {
        const fallback = cssVar[2] ? cssVar[2].trim() : '';
        if (typeof document === 'undefined')
            return fallback;

        value = getComputedStyle(document.documentElement).getPropertyValue(cssVar[1]).trim() || fallback;
    }

    if (!value)
        return '';

    if (value.startsWith('#') || value.startsWith('rgb') || value.startsWith('hsl'))
        return value;

    return resolvePaletteBackground(value);
}

function contrastingTextColor(background) {
    const literal = toLiteralColor(background);
    if (!literal)
        return '';

    try {
        // WCAG contrast ratios against white vs. black; the brighter pairing wins.
        const luminance = colors.luminosity(literal);
        const whiteContrast = 1.05 / (luminance + 0.05);
        const blackContrast = (luminance + 0.05) / 0.05;
        return blackContrast >= whiteContrast ? '#000000' : '#ffffff';
    }
    catch (e) {
        return '';
    }
}

function resolveFontSize(size) {
    if (!size)
        return '';

    let sz = 1;
    switch (size) {
        case 'xx-smaller': sz = 0.72; break;
        case 'x-smaller': sz = 0.8; break;
        case 'smaller': sz = 0.9; break;
        case 'normal': sz = 1; break;
        case 'larger': sz = 1.2; break;
        case 'x-larger': sz = 1.6; break;
        case 'xx-larger': sz = 2.4; break;
        case 'humongous': sz = 4; break;
        case 'colossal': sz = 6; break;
        case 'x-colossal': sz = 8; break;
        case 'xx-colossal': sz = 10; break;
    }
    return `${sz}rem`;
}

export default defineComponent({
    name: 'basic-avatar',
    components: { QAvatar },
    mixins: [BaseComponent],
    data: function () {
        return {
            textrenderer: null,
            tooltiprenderer: null,
            colorEval: null,
            fontSizeEval: null,
            cssClassEval: null,
            async_url: null,
            async_text: null,
            async_tooltip: null,
            Input: {},
            backgroundSize: null,
            textvalue: null,
            valueEval: null,
        }
    },
    created() {
        this.textvalue = this.controlData.Text || '';
        this.backgroundSize = `${(this.controlData.BackgroundSize || 50)}px`;
    },
    computed: {
        tooltip() {
            try {
                if(this.tooltiprenderer === null && this.controlData.Tooltip)
                    this.tooltiprenderer = utils.compile(this, this.Translate(this.controlData.Tooltip));
                if(this.tooltiprenderer)
                    return utils.evaluate(this.tooltiprenderer, this);
                return '';
            }
            catch (e) {
                utils.warn('Tooltip could not evaluate expression: ' + this.controlData.Tooltip + '; ' + e);
                return '';
            }
        },
        textString() {
            try {
                if(this.valueEval === null && this.textvalue)
                    this.valueEval = utils.compile(this, this.textvalue);

                if (this.valueEval) {
                    const res = utils.evaluate(this.valueEval, this);
                    return res;
                }
                return '';
            }
            catch (e) {
                utils.warn('Avatar textString could not evaluate expression: ' + this.value + '; ' + e);
                return '';
            }
        },
        fontSize() {
            try {
                if(this.fontSizeEval === null && this.controlData.FontSize)
                this.fontSizeEval = utils.compile(this, this.controlData.FontSize);
                if(this.fontSizeEval)
                return resolveFontSize(utils.evaluate(this.fontSizeEval, this));
                return '';
            }
            catch (e) {
                utils.warn('Font size could not evaluate expression: ' + this.controlData.FontSize + '; ' + e);
                return '14px';
            }
        },
        color() {
            try {
                if(this.colorEval === null && this.controlData.Color)
                    this.colorEval = utils.compile(this, this.controlData.Color);
                if(this.colorEval)
                    return utils.evaluate(this.colorEval, this);
                return '';
            }
            catch (e) {
                utils.warn('Color could not evaluate expression: ' + this.controlData.Color + '; ' + e);
                return '#444';
            }
        },
        cssClasses() {
            try {
                const classes = {};
                if (this.cssClassEval === null && this.controlData.CssClass)
                    this.cssClassEval = utils.compile(this, this.controlData.CssClass);
                if (this.cssClassEval) {
                    const evaluated = utils.evaluate(this.cssClassEval, this);
                    evaluated?.split(' ')?.forEach(item => { classes[item] = true; });
                }
                return classes;
            }
            catch (e) {
                utils.warn('CssClasses could not evaluate expression: ' + this.controlData.CssClass + '; ' + e);
                return {};
            }
        },
        customClasses() {
            let classes = { 'c-Avatar': true, [`c-name-${this.name || 'unnamed'}`]: true };

            let customClasses = "";
            if (this.styleHints && this.styleHints.CustomClasses) {
                customClasses = utils.resolveCustomClasses(this.styleHints, this);
                if (customClasses) {
                    customClasses = customClasses.split(' ');
                    for (let i = 0; i < customClasses.length; i++) {
                        classes[customClasses[i]] = true;
                    }
                }
            }

            return classes;
        },
        resolvedColor() {
            return resolveAvatarColor(this.color);
        },
        quasarAvatarColor() {
            if (!this.color || this.resolvedColor)
                return undefined;

            return this.color;
        },
        avatarStyles() {
            const styles = {};

            if (this.resolvedColor)
                styles.backgroundColor = this.resolvedColor;

            return styles;
        },
        contrastColor() {
            return contrastingTextColor(this.resolvedColor || this.quasarAvatarColor);
        },
        textStyles() {
            let styles = {};

            //if (this.fontSize)
            //    styles['font-size'] = this.fontSize;

            if (this.contrastColor)
                styles.color = this.contrastColor;

            return {
                ...styles,
                ...utils.resolveStyleHints(this.styleHints, this),
            };
        },
        size() {
            return { ...this.sizeStyle, display: 'flex', flexDirection: 'column' };
        },
    },
    methods: {
        preRenderComplete() {
            if (this.controlData.AsyncLookup === true) {
                this.async_url = utils.compile(this, this.controlData.AsyncOptions.URL);
                this.async_text = utils.compile(this, this.controlData.AsyncOptions.Text);
                this.async_tooltip = utils.compile(this, this.controlData.AsyncOptions.Tooltip);
            }
            this.finishRenderHandler(this);

            if (this.controlData.AsyncLookup === true) {
                this.LookupText();
            }
        },
        async LookupText() {
            try {
                const url = utils.evaluate(this.async_url, this);
                if (url) {
                    const res = await utils.api.get(url);
                    if (res)
                        this.Input.Data = res;
                    else
                        this.Input.Data = '';

                    // Update text with the expression from the async text property - should cause translation component to re-evaluate
                    this.textvalue = this.controlData.AsyncOptions.Text;
                    //this.textrenderer = this.async_text;
                    this.tooltiprenderer = this.async_tooltip;
                }
            }
            catch (e)
            {
                utils.warn('Async lookup failed to evaluate url: ' + this.controlData.AsyncOptions.URL + '; ' + e, e);
            }
        }
    },
    props: {
    },
    render() {
        try {
            if (!this.todisplay)
                return null;
    
            let avatar =
                <QAvatar
                    size={this.backgroundSize}
                    fontSize={this.fontSize}
                    color={this.quasarAvatarColor}
                    style={this.avatarStyles}
                    class={this.customClasses}
                    v-show={this.isvisible}
                >
                    <span style={this.textStyles}>
                        {this.textString}
                    </span>
                </QAvatar>;

            if (this.tooltip)
                avatar = utils.tt(this.tooltip, avatar);

            return avatar;
        }
        catch(e) {
            utils.error('Avatar Render failed', e);
            return <div>Avatar Failed to Render {e}</div>;
        }
    }
});